import type { ReactNode } from "react";

import type { CaseDetailOut } from "@/api";
import { formatDay } from "@/lib/format";

import styles from "./CancelledNotice.module.css";
import { LockIcon } from "./icons";

/** `CancellationOut` — read off the Vorgang (`CaseDetailOut.cancellation`). */
type Cancellation = NonNullable<CaseDetailOut["cancellation"]>;

interface CancelledNoticeProps {
  /** Who cancelled it, when and why — `null` while that is not loaded (or the Vorgang has none). */
  cancellation: Cancellation | null;
  /**
   * The one way back. In the record the restore itself; on the board and on
   * the calculation the way to the record, where it is (spec 2026-10-15 §5).
   */
  action: ReactNode;
  /** The sentence's id — for the action's `aria-describedby`. */
  id?: string;
}

/**
 * „Storniert am … von … — Grund: …“ (spec 2026-10-15 §5), the one notice a
 * cancelled Vorgang shows wherever it is opened: the record, the board, the
 * calculation. A shared block because those three may not import each other
 * (R-W1), and because the same state should look the same everywhere.
 *
 * A state of its own, not a refusal: no status red — nothing is wrong with
 * the data, the Vorgang is set aside (CI §2.1). The lock says so beside the
 * word, the word says so without it. Without the details (still loading, or
 * the Vorgang has none) it says „Storniert“ and nothing more.
 */
export function CancelledNotice({ cancellation, action, id = "case-cancelled" }: CancelledNoticeProps) {
  return (
    <div role="group" aria-label="Storniert" className={styles.notice}>
      <LockIcon className={styles.icon} />
      <p id={id} className={styles.text}>
        <b>Storniert</b>
        {cancellation !== null ? (
          <>
            {" "}
            am <span className="tabular">{formatDay(cancellation.at)}</span>
            {cancellation.by !== null ? <> von {cancellation.by}</> : null}
            {cancellation.reason !== null ? <> — Grund: {cancellation.reason}</> : null}
          </>
        ) : null}
      </p>
      <div className={styles.action}>{action}</div>
    </div>
  );
}
